// NexFlow Hub — Theme Registry
// Single source of truth for all available themes. Add new themes here (e.g. NexFlow Blue,
// or PM client-branded themes) and they will automatically light up in the toggle/picker.
//
// Each theme entry:
//   id          → string key persisted to DB (must match backend allow-list)
//   label       → human-readable name shown in the Appearance picker
//   className   → CSS class applied to <html> and <body>
//   isDark      → whether the theme is "dark family" (drives Sun/Moon icon swap)
//   description → short tagline for the picker
//   accentHex   → swatch shown in the picker
//   bgHex       → background swatch shown in the picker
//   experimental→ if true, only shown when SHOW_EXPERIMENTAL_THEMES is true
//
// Branding rule: every theme must preserve NexFlow's black/gold identity in some form.

export const THEMES = {
  dark: {
    id: 'dark',
    label: 'Dark',
    className: 'theme-dark',
    isDark: true,
    description: 'Default NexFlow black/gold console.',
    bgHex: '#0A0A0A',
    accentHex: '#D4AF37',
  },
  light: {
    id: 'light',
    label: 'Light',
    className: 'theme-light',
    isDark: false,
    description: 'White background, black text, gold accents — high desktop readability.',
    bgHex: '#FFFFFF',
    accentHex: '#B8941F',
  },
  // ----- Reserved / future themes (kept off by default) -----
  'nexflow-blue': {
    id: 'nexflow-blue',
    label: 'NexFlow Blue',
    className: 'theme-nexflow-blue',
    isDark: true,
    description: 'Midnight navy with gold accents (preview).',
    bgHex: '#0B1428',
    accentHex: '#D4AF37',
    experimental: true,
  },
};

export const DEFAULT_THEME_ID = 'dark';

// All theme IDs the backend should accept. Keep in sync server-side.
export const VALID_THEME_IDS = Object.keys(THEMES);

// All className strings the runtime might toggle on <html>/<body>.
export const ALL_THEME_CLASSNAMES = Object.values(THEMES).map((t) => t.className);

// Roles that can toggle their theme. Customers + VIPs stay locked to dark.
export const THEME_ELIGIBLE_ROLES = new Set([
  'super_admin', 'admin', 'office_staff', 'service_manager', 'construction_manager',
  'manager', 'technician', 'employee',
  'property_manager', 'asset_manager', 'maintenance_supervisor', 'pm_maintenance',
]);

// URL prefixes that must always render in dark mode regardless of user preference.
// These are the customer-facing / unauthenticated surfaces — branding stays consistent.
export const FORCE_DARK_PATH_PREFIXES = [
  '/login', '/register', '/verify', '/accept-invite', '/forgot',
  '/customer', '/vip',
  '/pay/', '/po-approve/', '/sms-opt-in', '/sms-consent', '/sms-compliance',
  '/terms', '/privacy', '/public/',
];

export const pathIsForcedDark = (pathname) => {
  if (!pathname || pathname === '/' || pathname === '') return true;
  return FORCE_DARK_PATH_PREFIXES.some((p) => pathname.startsWith(p));
};

export const getThemeById = (id) => THEMES[id] || THEMES[DEFAULT_THEME_ID];

// Themes visible in the user-facing picker (filters experimental unless flag enabled).
export const listAvailableThemes = ({ includeExperimental = false } = {}) =>
  Object.values(THEMES).filter((t) => includeExperimental || !t.experimental);
